import { mergeCandidateNotes } from "@/lib/db/event_venue_candidate";
import type { PostgrestError, SupabaseClient } from "@supabase/supabase-js";

/** The `manual_pipeline_status` a hand-off records on the candidate. */
const HANDOFF_PIPELINE_STATUS = "connected";

/**
 * Builds the `event_venue_candidate` fields to write when a Northstar RFP
 * hand-off occurs. Merges `manual_pipeline_status: "connected"` into the
 * existing `internal_candidate_notes` JSON blob via the shared merge helper so
 * any other keys already stored on the row (e.g. `tracker_notes`) survive.
 *
 * @param existingNotesRaw - The candidate's current `internal_candidate_notes`
 *   value (JSON-stringified text column), or null for a fresh candidate.
 * @returns The fields to spread into the candidate insert/update.
 */
export function buildHandoffCandidateFields(existingNotesRaw: string | null): {
  connected_client_to_hotel: true;
  internal_candidate_notes: string;
} {
  return {
    connected_client_to_hotel: true,
    internal_candidate_notes: mergeCandidateNotes(existingNotesRaw, {
      manual_pipeline_status: HANDOFF_PIPELINE_STATUS,
    }),
  };
}

/**
 * Writes {@link buildHandoffCandidateFields} to a candidate row after a
 * successful Northstar hand-off send. Called from inside `sendRFPEmails`
 * (`components/ui/utils.ts`) — the one place shared by every RFP-send surface
 * that can reach a hand-off event (the admin `/admin/fetch` panel and the
 * chain-rep add-venue flow), keyed off its `handoffClientEmail` param, so
 * every venue actually sent to gets flagged, including chain-rep-fallback
 * sends where the caller only sees an aggregate result. `sendRfpToVenue`
 * doesn't use this — it folds the same fields into its existing candidate
 * insert/update instead of a separate write, since it already has that row in
 * flight.
 *
 * @param supabase - Client authorized to write `event_venue_candidate` (service-role
 *   or an admin-authenticated session — RLS grants admins full access).
 * @param eventId - The event ID.
 * @param venueStaticId - The venue static ID.
 * @param existingNotesRaw - The candidate's current `internal_candidate_notes`
 *   value, or null for a fresh candidate.
 * @returns The write error, or null on success — callers log it rather than throw,
 *   since the email already sent by the time this runs.
 */
export async function flagHandoffCandidate(
  supabase: SupabaseClient,
  eventId: number,
  venueStaticId: number,
  existingNotesRaw: string | null
): Promise<PostgrestError | null> {
  const { error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update(buildHandoffCandidateFields(existingNotesRaw))
    .eq("event_id", eventId)
    .eq("venue_static_id", venueStaticId);
  return error;
}
